import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Phone, EnvelopeSimple, MapPin, CheckCircle } from "@phosphor-icons/react";
import { services as serviceList } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  ...serviceList.map((s) => s.name),
  "Other / Multiple",
];

const inputCls =
  "w-full rounded-none border border-[#C4C4B5] bg-[#F4F4EB] px-4 py-3 text-[#111110] placeholder-[#595955]/60 focus:border-[#E5231A] focus:ring-0 focus:outline-none transition-colors";
const labelCls =
  "block text-xs font-bold uppercase tracking-[0.2em] text-[#111110] mb-2";

export default function Contact() {
  const location = useLocation();
  const preselectedService = location.state?.service;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service:
      preselectedService && SERVICE_OPTIONS.includes(preselectedService)
        ? preselectedService
        : SERVICE_OPTIONS[0],
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (preselectedService && SERVICE_OPTIONS.includes(preselectedService)) {
      setForm((f) => ({ ...f, service: preselectedService }));
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedService]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill out your name, phone, email and a short message.");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Request sent — Michael will be in touch shortly.");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: SERVICE_OPTIONS[0],
        location: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        "Something went wrong. Please call (325) 513-3501.";
      toast.error(typeof detail === "string" ? detail : "Please check your entries and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F4F4EB] py-24 md:py-32 border-b border-[#C4C4B5]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="uppercase text-xs tracking-[0.25em] font-bold text-[#E5231A] mb-4">
            Get a Quote
          </div>
          <h2
            data-testid="contact-heading"
            className="font-display font-black uppercase tracking-tighter text-[#111110] text-4xl sm:text-5xl md:text-6xl leading-[0.95]"
          >
            Tell Us About The Job.
            <br />
            We&apos;ll Handle The Rest.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#595955] leading-relaxed max-w-md">
            Fill out the form and Michael will reach out personally — usually
            the same day. Prefer to talk? Give us a call.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="tel:+13255133501"
              data-testid="contact-info-phone"
              className="flex items-center gap-4 border border-[#111110] bg-[#EAEAE0] p-4 hover:bg-[#E5231A] hover:text-[#F4F4EB] transition-colors group"
            >
              <Phone size={22} weight="fill" className="text-[#E5231A] group-hover:text-[#F4F4EB]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Call</div>
                <div className="font-display font-extrabold text-lg">(325) 513-3501</div>
              </div>
            </a>
            <a
              href="mailto:m.schaefer@schaeferservicestx.com"
              data-testid="contact-info-email"
              className="flex items-center gap-4 border border-[#111110] bg-[#EAEAE0] p-4 hover:bg-[#E5231A] hover:text-[#F4F4EB] transition-colors group"
            >
              <EnvelopeSimple size={22} weight="fill" className="text-[#E5231A] group-hover:text-[#F4F4EB]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Email</div>
                <div className="font-display font-extrabold text-lg break-all">m.schaefer@schaeferservicestx.com</div>
              </div>
            </a>
            <div
              data-testid="contact-info-area"
              className="flex items-center gap-4 border border-[#111110] bg-[#EAEAE0] p-4"
            >
              <MapPin size={22} weight="fill" className="text-[#E5231A]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Service Area</div>
                <div className="font-display font-extrabold text-lg">
                  Abilene, TX &amp; Surrounding
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="border-2 border-[#111110] bg-[#EAEAE0] p-8 md:p-12 text-center"
            >
              <CheckCircle size={56} weight="duotone" className="text-[#E5231A] mx-auto" />
              <h3 className="mt-6 font-display font-black uppercase text-3xl md:text-4xl tracking-tight text-[#111110]">
                Request Received
              </h3>
              <p className="mt-4 text-[#595955] max-w-md mx-auto">
                Thanks for reaching out. Michael will call or email you shortly.
                If you need us faster, give us a ring at (325) 513-3501.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-8 btn-hard inline-flex items-center gap-2 bg-[#111110] text-[#F4F4EB] font-bold uppercase text-sm tracking-wider px-6 py-3 border border-[#111110]"
              >
                Send Another Request
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border-2 border-[#111110] bg-[#EAEAE0] p-6 md:p-10"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="name">
                    Your Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-input-name"
                    className={inputCls}
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="phone">
                    Phone
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-input-phone"
                    className={inputCls}
                    placeholder="(325) 555-1234"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls} htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className={inputCls}
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="service">
                    Service Needed
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    data-testid="contact-input-service"
                    className={inputCls}
                  >
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="location">
                    Project Location
                  </label>
                  <input
                    id="location"
                    name="location"
                    type="text"
                    value={form.location}
                    onChange={onChange}
                    data-testid="contact-input-location"
                    className={inputCls}
                    placeholder="City / Address (optional)"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls} htmlFor="message">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    className={inputCls}
                    placeholder="Tell us what you need done, size of the property, timeline, etc."
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-[#595955] max-w-sm">
                  By submitting, you agree to be contacted about your project.
                  We don&apos;t share your info.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="btn-hard inline-flex items-center gap-2 bg-[#E5231A] text-[#F4F4EB] font-bold uppercase text-sm tracking-wider px-7 py-4 border border-[#111110] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Request"}
                  <ArrowRight size={18} weight="bold" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
